import WorkorderChecklistFooter from '@/components/Movements/WorkOrders/WorkorderChecklist/ChecklistFooter';
import { useWorkorderChecklistContext } from '@/components/Movements/WorkOrders/WorkorderChecklist/WorkorderChecklistProvider';
import { DefaultRouteTabs, FloatingFormItem } from '@/components/_common';
import useChecklistValidRoutes from '@/hooks/useChecklistValidRoutes';
import { PageProps } from '@/types/PageProps';
import { Helmet } from 'react-helmet';
import { useIntl } from 'react-intl';
import { useCallback, useEffect, useRef, useState } from 'react';
import './styles.less';
import {
  useWorkOrderChecklistDamageCreate,
  useWorkOrderChecklistDamageList,
} from '@/core/domain/workorder/WorkOrdersChecklist/WorkOrdersChecklist.hooks';
import { Alert, Button, Col, Form, Modal, Radio, Row, Select } from 'antd';
import { checklistDamages } from '@/core/domain/workorder/WorkOrdersChecklist/WorkOrdersChecklist.DamageConstant';
import { WorkorderChecklistItemType } from '@/components/Movements/WorkOrders/WorkorderChecklist/WorkorderChecklistProvider/WorkorderChecklistProvider.types';
import {
  WorkOrderChecklistDamage,
  WorkOrderChecklistDamageAnswer,
} from '@/core/domain/workorder/WorkOrdersChecklist/WorkOrdersChecklist.types';
import OldChecklist from '@/components/Movements/WorkOrders/WorkorderChecklist/OldChecklist';
import { useOldChecklistDamage } from '@/core/domain/workorder/WorkOrderOldChecklist/WorkOrderOldChecklist.hooks';
import ModalLoadingOverlay from '@/components/_common/ModalLoadingOverlay';
import { CreateCustomerVehicleRequest } from '@/core/domain/people/CustomerVehicles/CustomerVehicles.types';
import { Group } from 'antd/lib/avatar';
import VehicleModal from '@/components/_common/_VehicleModal';
import { useDisclosure } from '@/hooks';
import { VehicleReturnValues } from '@/components/_common/_VehicleModal/VehicleModal.types';
import { useUpdateCustomerVehicle } from '@/core/domain/people/CustomerVehicles/CustomerVehicles.hooks';
import { queryClient } from '@/Root';
import { handlerImageCar } from '@/utils/functions/handlerImageCar';
import { uploadArchive } from '@/core/domain/archive/Archive/Archive.service';
import { useMatrix } from '@/core/domain/enterprise/Matrix/Matrix.hooks';
import ChecklistDamageModal from '@/components/Movements/WorkOrders/WorkorderChecklist/ChecklistDamageModal';

const WorkorderChecklistDamagePage: React.FC<PageProps> = ({ route }) => {
  const intl = useIntl();
  const {
    checklistItemId,
    checklistItemType,
    workorderId,
    diagnosticId,
    boschChecklist,
    vehicleSelected,
    customerId,
  } = useWorkorderChecklistContext();
  const routeParent = useChecklistValidRoutes({
    route,
    checklistItemType,
    workorderId,
    boschChecklist,
  });

  const carRef = useRef<SVGSVGElement>(null);
  const [coords, setCoords] = useState<{ x: number; y: number }>();

  const [form] = Form.useForm<{ damage: string }>();

  const [damageType, setDamageType] = useState('new');
  const [damage, setDamage] = useState<string>();

  const [modalVehicleVisible, modalVehicleAction] = useDisclosure(false);
  const [modalSelectDamage, modalSelectDamageAction] = useDisclosure(false);

  const customerVehicleUpdate = useUpdateCustomerVehicle(customerId);

  const damageCreate = useWorkOrderChecklistDamageCreate();
  const damageList = useWorkOrderChecklistDamageList({
    diagnosticId:
      checklistItemType == WorkorderChecklistItemType.Diagnostic
        ? checklistItemId
        : undefined,
    workOrderComplementId:
      checklistItemType == WorkorderChecklistItemType.Workorder
        ? checklistItemId
        : undefined,
  });
  const matrix = useMatrix();
  const matrixSettings = matrix.data?.matrixSettings;

  const svgProps = {
    ref: carRef,
    className: 'car-sides',
    key: damageList.data?.length,
  };

  const oldDamageList = useOldChecklistDamage({
    diagnosticId:
      checklistItemType == WorkorderChecklistItemType.Diagnostic
        ? checklistItemId
        : undefined,
    workOrderId:
      checklistItemType == WorkorderChecklistItemType.Workorder
        ? workorderId
        : undefined,
  });

  const checklistDamagesOptions = checklistDamages.map((cur, index) => ({
    value: cur.value,
    label: `${index + 1} - ${cur.label}`,
  }));

  useEffect(() => {
    carRef.current?.addEventListener('click', svgClick);

    return () => carRef.current?.removeEventListener('click', svgClick);
  }, [
    carRef.current,
    damageList.dataUpdatedAt,
    damageType,
    matrix.dataUpdatedAt,
  ]);

  useEffect(() => {
    if (damageList.data?.length) {
      damageList.data?.forEach(setRect);
    }
  }, [damageList.dataUpdatedAt, damageType, vehicleSelected]);

  function svgClick(ev: unknown) {
    if (matrixSettings?.isDamageSelectedFirst) modalSelectDamageAction.open();

    setCoords(getCoords(ev as PointerEvent));
  }

  function getCoords(event: PointerEvent) {
    if (!carRef.current) return;

    const svgRect = carRef.current.getBoundingClientRect();

    return {
      x: Number((event.clientX - svgRect.left).toFixed(2)),
      y: Number((event.clientY - svgRect.top).toFixed(2)),
    };
  }

  function setRect(damage: WorkOrderChecklistDamage) {
    if (!carRef.current) return;

    const itemIndex = checklistDamages.findIndex(
      (item) => item.value === damage.damage,
    );
    const damageItem = checklistDamages[itemIndex];
    const damageCode = String(itemIndex + 1).padStart(2, '0');

    const svgns = 'http://www.w3.org/2000/svg';

    const g = document.createElementNS(svgns, 'g');
    const rect = document.createElementNS(svgns, 'rect');

    const x = damage.x - 15;
    const y = damage.y - 15;

    g.setAttribute('x', x.toString());
    g.setAttribute('transform', `matrix(1, 0, 0, 1, ${x}, ${y})`);
    g.classList.add('car-damage');

    rect.setAttribute('rx', '15');
    rect.setAttribute('ry', '15');
    rect.onclick = (e) => {
      e.stopPropagation();
      e.preventDefault();

      Modal.confirm({
        title: 'Remover avaria',
        content: `Deseja remover a avaria "${damageCode} - ${damageItem?.label}"?`,
        onOk: () => handleUpdate(damage),
        okText: 'Remover',
        okType: 'danger',
      });
    };

    const text = document.createElementNS(svgns, 'text');
    text.setAttribute('x', '15');
    text.setAttribute('y', '16');
    text.setAttribute('text-anchor', 'middle');
    text.setAttribute('dominant-baseline', 'middle');
    text.setAttribute('fill', 'black');
    const textNode = document.createTextNode(damageCode);

    const textTooltip = document.createElementNS(svgns, 'text');
    textTooltip.setAttribute('x', '0');
    textTooltip.setAttribute('y', '-7');
    textTooltip.setAttribute('fill', 'black');
    textTooltip.classList.add('car-damage-tooltip');

    const textTooltipNode = document.createTextNode(damageItem?.label);

    text.appendChild(textNode);
    textTooltip.appendChild(textTooltipNode);
    g.appendChild(rect);
    g.appendChild(text);
    g.appendChild(textTooltip);
    carRef.current?.appendChild(g);

    textTooltip.setAttribute('x', String(-(textTooltip.clientWidth + 30) / 2));
  }

  const handleCreate = useCallback(async () => {
    if (!coords || !damage) return;

    const answers =
      damageList.data?.map<WorkOrderChecklistDamageAnswer>((item) => ({
        id: item.id,
        damage: item.damage,
        position: { x: item.x, y: item.y },
      })) || [];

    answers.push({ damage, position: coords });

    if (checklistItemType === WorkorderChecklistItemType.Diagnostic) {
      await damageCreate.mutateAsync({
        diagnosticId: checklistItemId,
        answers,
      });
    } else if (checklistItemType === WorkorderChecklistItemType.Workorder) {
      await damageCreate.mutateAsync({
        workOrderComplementId: checklistItemId,
        answers,
      });
    }

    setCoords(undefined);
    if (matrixSettings?.isDamageSelectedFirst) setDamage(undefined);
  }, [coords, damage]);

  useEffect(() => {
    handleCreate();
  }, [handleCreate]);

  async function handleUpdate(damage: WorkOrderChecklistDamage) {
    try {
      const answers =
        damageList.data
          ?.filter((item) => item.id !== damage.id)
          .map<WorkOrderChecklistDamageAnswer>((item) => ({
            id: item.id,
            damage: item.damage,
            position: { x: item.x, y: item.y },
          })) || [];

      if (checklistItemType === WorkorderChecklistItemType.Diagnostic) {
        await damageCreate.mutateAsync({
          diagnosticId: checklistItemId,
          answers,
        });
      } else if (checklistItemType === WorkorderChecklistItemType.Workorder) {
        await damageCreate.mutateAsync({
          workOrderComplementId: checklistItemId,
          answers,
        });
      }
    } catch {}
  }

  const onVehicleSave = async (values: VehicleReturnValues) => {
    if (customerId && values) {
      let image = values.image;

      if (!!values.imageUpload) {
        const result = await uploadArchive({
          file: [values.imageUpload as Blob],
          isPublic: false,
        });

        image = result.filePath;
      }

      const obj: CreateCustomerVehicleRequest = {
        model: {
          id: values.modelId,
          brand: { id: values.brandId, image: values.model?.brand.image },
        },
        fuel: { id: values.fuelId },
        color: { id: values.colorId },
        licensePlate: values.licensePlate,
        yearFabrication: Number(values.yearFabrication),
        yearModel: Number(values.yearModel),
        chassi: values.chassi,
        fleet: values.fleet,
        km: values.km,
        renavam: values.renavam,
        type: values?.type,
        active: true,
        accessories: values.accessories || [],
        image,
        cylinders: values.cylinders,
        engineCapacity: values.engineCapacity,
        engineName: values.engineName,
        engineTechnicalCode: values.engineTechnicalCode,
        horsePower: values.horsePower,
        release: values.release,
        valves: values.valves,
        line: values.line,
        airIntake: values.airIntake,
        rearBrake: values.rearBrake,
        frontBrake: values.frontBrake,
        brakeSystem: values.brakeSystem,
        steeringSystem: values.steeringSystem,
        tractionSystem: values.tractionSystem,
        injectionSystem: values.injectionSystem,
        transmissionType: values.transmissionType,
        engineConfiguration: values.engineConfiguration,
      };

      await customerVehicleUpdate.mutateAsync({ ...obj, id: values.vehicleId });

      await queryClient.invalidateQueries(['workOrders', workorderId]);
      await queryClient.invalidateQueries(['diagnostic', diagnosticId]);

      await damageList.refetch();
    }
  };

  useEffect(() => {
    if (!!matrixSettings) {
      const value = matrixSettings?.isDamageSelectedFirst
        ? undefined
        : checklistDamages[0].value;

      setDamage(value);
      form.setFieldValue('damage', value);
      setCoords(undefined);
    }
  }, [matrix.dataUpdatedAt]);

  return (
    <DefaultRouteTabs
      route={routeParent}
      baseKey={String(checklistItemId)}
      basePath={`/app/movements/workorderChecklist/${checklistItemId}`}
    >
      <Helmet title={intl.formatMessage({ id: route.name })} />

      {matrix.isLoading && (
        <ModalLoadingOverlay
          messages={
            matrix.isLoading
              ? ['Carregando configurações...']
              : damageList.isLoading
              ? ['Carregando lista de avarias...']
              : ['Carregando...']
          }
        />
      )}

      {!!oldDamageList.data?.length && (
        <Radio.Group
          value={damageType}
          onChange={({ target }) => setDamageType(target.value)}
          style={{ marginBottom: 24 }}
        >
          <Radio.Button value="new">Nova versão</Radio.Button>

          <Radio.Button value="old">Versão antiga (Visualização)</Radio.Button>
        </Radio.Group>
      )}

      {damageType === 'new' && (
        <Alert
          className="car-sides-alert"
          type="info"
          description="Arraste a imagem na horizotal para visualizar a imagem completa"
          style={{ marginBottom: 48 }}
          closable
        />
      )}

      {!vehicleSelected?.type && !damageList.isLoading && (
        <Alert
          closable
          type="warning"
          message={
            <Group>
              <b>
                {(damageList.data || []).length > 0
                  ? 'O checklist atual contém avarias selecionadas, porém o veículo não tem um tipo definido. Isso pode alterar a posição das avarias.'
                  : 'O veículo associado ao orçamento não tem um tipo definido.'}
              </b>
            </Group>
          }
          description={
            <Row>
              <Col span={24}>
                <Button onClick={modalVehicleAction.toggle}>
                  Definir tipo do veículo
                </Button>
              </Col>
            </Row>
          }
          style={{ marginBottom: 24 }}
        />
      )}

      {damageCreate.isLoading && (
        <ModalLoadingOverlay messages={['Salvando avaria do carro...']} />
      )}

      <Row justify="center">
        {damageType === 'new' && (
          <>
            {!matrixSettings?.isDamageSelectedFirst && (
              <Col span={24} lg={8}>
                <Form form={form} style={{ marginBottom: 24 }}>
                  <FloatingFormItem name="damage" label="Avaria">
                    <Select
                      value={damage}
                      options={checklistDamagesOptions}
                      onChange={setDamage}
                    />
                  </FloatingFormItem>
                </Form>
              </Col>
            )}

            <Col span={24} style={{ overflowX: 'auto', textAlign: 'center' }}>
              {handlerImageCar(svgProps, vehicleSelected?.type)}
            </Col>
          </>
        )}

        {damageType === 'old' && (
          <Col span={24}>
            <OldChecklist damageList={oldDamageList.data || []} />
          </Col>
        )}
      </Row>

      <WorkorderChecklistFooter />

      <ChecklistDamageModal
        open={modalSelectDamage}
        onCancel={modalSelectDamageAction.close}
        onSelectDamage={(value) => setDamage(value)}
      />

      <VehicleModal
        onOk={onVehicleSave}
        open={modalVehicleVisible}
        onCancel={modalVehicleAction.toggle}
        vehicleId={vehicleSelected?.id}
        customerId={customerId ? Number(customerId) : undefined}
        consultLicensePlate
      />
    </DefaultRouteTabs>
  );
};

export default WorkorderChecklistDamagePage;
